import {html, css, LitElement, nothing} from 'lit';
import {timeOut, microTask} from '@polymer/polymer/lib/utils/async.js';
import {Debouncer} from '@polymer/polymer/lib/utils/debounce.js';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {HotkeyBehavior} from '../behaviors/hotkey-behavior.js';
import {TooltipBehavior} from '../behaviors/tooltip-behavior';

class KeliBrowseHeaderCell extends ScopeBinding(TooltipBehavior(HotkeyBehavior(LitElement))) {
  static styles = css`
      :host {
          display: table-cell;
          white-space: nowrap;
          vertical-align: top;
          -webkit-touch-callout: none;
          user-select: none;
          cursor: default;
          border-right: 1px solid var(--keli-browse-toolbar-border-color);
          box-sizing: border-box;
          width: 150px;
          min-width: 150px;
          max-width: 150px;
          height: 100%;
      }

      #cell {
          box-sizing: border-box;
          padding: 2px 6px 0 6px;
          position: relative;
          font-size: 12px;
      }

      #label {
          font-weight: 700;
          text-transform: uppercase;
          display: block;
          overflow: hidden;
          padding: 2px 0;
      }

      :host(.sorted) #label {
          margin-right: 18px;
      }

      #resizeHandle {
          cursor: ew-resize;
          right: -4px;
          top: 0;
          bottom: 0;
          position: absolute;
          min-width: 8px;
          min-height: 8px;
          opacity: 0;
          z-index: var(--keli-zindex-browse-resizehandle);
      }

      #resizeHandle.disabled {
          display: none;
      }

      #sortIcon {
          position: absolute;
          right: 6px;
          top: 2px;
          font-size: 16px;
      }

      #filter {
          position: relative;
          top: -2px;
          display: block;
          -webkit-appearance: textfield;
          box-sizing: border-box;
          width: 100%;
          outline: none;
          color: var(--keli-text);
          background-color: var(--keli-field-background);
          border: none;
          border-bottom: 2px solid var(--keli-field-border-color);
          transition: border-bottom var(--keli-transition-fast);
          text-transform: var(--keli-browse-header-cell-text-transform);
      }

      #filter:focus {
          border-color: var(--keli-field-border-color-active);
      }

      #filter.hidden {
          display: none;
      }

      :host(.slottedContentOnly) #label {
          display: none;
      }

      :host(.slottedContentOnly) #sortIcon {
          display: none;
      }

      :host(.slottedContentOnly) #filter {
          display: none;
      }
  `;

  static properties = {
    caption: {type: String, scopeVar: true},
    columnMinimumWidth: {type: Number},
    currentValue: {type: String, state: true},
    filterable: {type: Boolean, scopeVar: true},
    filterValue: {type: String, scopeVar: {options: {confine: true}}},
    focused: {type: Boolean},
    justify: {type: String, scopeVar: true},
    justifyCell: {type: String, state: true},
    keliScopeVarOptions: {type: Object},
    resizable: {type: Boolean, scopeVar: true},
    slottedContentOnly: {type: Boolean},
    sortable: {type: Boolean, scopeVar: true},
    sortDirection: {type: Number, reflect: true},
    sortIndex: {type: Number},
    textCasing: {type: String, scopeVar: true},
    tooltipText: {type: String},
    vis: {type: Boolean, scopeVar: {options: {confine: true}}},
    width: {type: Number, scopeVar: {options: {view: true}}},
  };

  constructor() {
    super();

    this.Sort = Object.freeze({'ASCENDING': 1, 'DESCENDING': 2});
    this.columnMinimumWidth = 30;
    this.keliScopeVarOptions = {confine: true};
    this._boundHandleResizeHandleMouseMove = this.handleResizeHandleMouseMove.bind(this);
    this._boundHandleResizeHandleMouseUp = this.handleResizeHandleMouseUp.bind(this);
  }

  render() {
    return html`
        <div id="cell" @click="${this.handleClick}">
            <span id="label">${this.caption ?? nothing}</span>
            <sl-icon id="sortIcon"></sl-icon>
            <input id="filter" type="search" autocomplete="off"
                   @click="${this.handleFilterClick}"
                   @focus="${this.handleFilterFocus}"
                   @blur="${this.handleFilterBlur}"
                   @input="${this.handleFilterInput}"
                   @mousedown="${this.handleMouseDown}"
                   @mouseup="${this.handleMouseUp}">
            <slot name="multiselect"></slot>
            <span id="resizeHandle" data-direction="e"
                  @click="${this.handleResizeHandleClick}"
                  @dblclick="${this.handleResizeHandleDoubleClick}"
                  @mousedown="${this.handleResizeHandleMouseDown}"></span>
        </div>`;
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    for (const property of changedProperties.keys()) {
      if (this[`${property}Changed`]) {
        this[`${property}Changed`](this[property]);
      }
    }

    if (changedProperties.has('caption') || changedProperties.has('width')) {
      this.tooltipText = this.computeTooltipText(this.caption, this.width);
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.addHotkeyListener('raw-listener', this, {forceDefaults: true});
    this.registerHotkey(KeliInterface.Hotkeys.browse.filter.send, {
      down: event => {
        if (this._currentValueChanged && this._currentValueChanged.isActive()) {
          event.stopPropagation();
          this._currentValueChanged.flush();
        }
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.browse.filter.deleteCharacter, {
      down: event => {
        if (this.renderRoot.querySelector('#filter').value.length) {
          event.stopPropagation();
        }
      }
    }, 'raw-listener');

    this.dispatchEvent(new CustomEvent('browseHeaderCellAttached', {
      bubbles: true,
      composed: true,
      detail: {
        cell: this,
        column: Array.prototype.indexOf.call(this.parentNode.children, this) + 1
      }
    }));
    this.dispatchEvent(new CustomEvent('panelElementCreated', {
      bubbles: true,
      composed: true,
      detail: {element: this}
    }));

    this.addTooltip(this, 'tooltipText');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.dispatchEvent(new CustomEvent('browseHeaderCellDetached', {
      bubbles: true,
      composed: true,
      detail: {element: this}
    }));
    if (this.browse) {
      this.browse = null;
    }
  }

  captionChanged() {
    this.calculateTextWidth(true);
  }

  widthChanged(newValue) {
    this.style.width = this.style.maxWidth = this.style.minWidth = `${newValue}px`;

    if (this.browse && this.keliname) {
      this.browse.dispatchEvent(new CustomEvent('columnWidthChanged', {
        detail: {keliname: this.keliname, width: newValue}
      }));
    }
  }

  justifyChanged(newValue) {
    if (this.browse && this.keliname) {
      this.browse.dispatchEvent(new CustomEvent('columnJustifyChanged', {
        detail: {keliname: this.keliname, justify: newValue}
      }));
    }
  }

  sortDirectionChanged(newValue) {
    const iconElement = this.renderRoot.querySelector('#sortIcon');
    if (iconElement) {
      switch (newValue) {
        case this.Sort.ASCENDING:
          iconElement.name = 'chevron-up';
          this.classList.add('sorted');
          break;
        case this.Sort.DESCENDING:
          iconElement.name = 'chevron-down';
          this.classList.add('sorted');
          break;
        default:
          iconElement.name = '';
          this.classList.remove('sorted');
      }
    }
  }

  textCasingChanged(newValue) {
    let transform;
    const property = newValue?.toLowerCase();
    if (property && ['uppercase', 'lowercase', 'capitalize'].includes(property)) {
      transform = property;
    }
    this.style.setProperty('--keli-browse-header-cell-text-transform', transform || '');
  }

  resizableChanged(newValue) {
    this.renderRoot.querySelector('resizeHandle')?.classList.toggle('disabled', !newValue);
  }

  filterableChanged(newValue) {
    const browseWasFocused = this.browse?.focused;
    const filterElement = this.renderRoot.querySelector('#filter');
    filterElement.classList.toggle('hidden', !newValue);
    filterElement.disabled = !newValue;
    if (newValue === false && browseWasFocused) {
      this.browse.dispatchEvent(new CustomEvent('browseHeaderCellFilterableChanged', {detail: {element: this}}));
    }
  }

  slottedContentOnlyChanged(newValue) {
    this.classList.toggle('slottedContentOnly', newValue);
  }

  justifyCellChanged(newValue) {
    this.renderRoot.querySelector('#cell').style.textAlign = (newValue || '');
  }

  filterValueChanged(newValue, oldValue) {
    const isInitialFocusEvent = (!oldValue && !this.currentValue);
    const filterElement = this.renderRoot.querySelector('#filter');

    if (this.currentValue !== newValue) {
      this.currentValue = newValue;
    }
    if (newValue !== filterElement.value) {
      filterElement.value = newValue;
    }

    if (isInitialFocusEvent && this.focused && !filterElement.disabled) {
      filterElement.focus();
      filterElement.select();
    }
  }

  currentValueChanged() {
    this._currentValueChanged = Debouncer.debounce(this._currentValueChanged, timeOut.after(500), () => {
      if (this.filterValue === this.currentValue) {
        if (this.browse) {
          this.browse.currentlyFiltering = false;
        }
      } else {
        this.filterValue = this.currentValue;
      }
    });
  }

  focusElement() {
    this.browse?.focus();
    if (this.browse?.focused) {
      const filterElement = this.renderRoot.querySelector('#filter');
      filterElement.focus();
      filterElement.select();
    }
  }

  handleResizeHandleClick(event) {
    event.stopImmediatePropagation();
  }

  handleResizeHandleDoubleClick() {
    let maxCellWidth = this.calculateTextWidth();
    if (this.browse) {
      const rows = this.browse.$.body.querySelectorAll('keli-browse-row');
      rows.forEach(row => {
        const cell = row.querySelector(`[data-keliname="${this.keliname}"]`);
        if (cell) {
          const cellWidth = row.calculateTextWidth(cell);
          if (cellWidth > maxCellWidth) {
            maxCellWidth = cellWidth;
          }
        }
      });
    }
    this.width = maxCellWidth;
  }

  handleResizeHandleMouseDown(event) {
    event.stopImmediatePropagation();
    this._startWidth = parseInt(window.getComputedStyle(this, null).getPropertyValue('width'), 10);
    this._startX = event.clientX;

    document.addEventListener('mousemove', this._boundHandleResizeHandleMouseMove);
    document.addEventListener('mouseup', this._boundHandleResizeHandleMouseUp);
  }

  handleResizeHandleMouseUp() {
    document.removeEventListener('mousemove', this._boundHandleResizeHandleMouseMove);
    document.removeEventListener('mouseup', this._boundHandleResizeHandleMouseUp);
  }

  handleResizeHandleMouseMove(event) {
    let newWidth = this._startWidth + (event.clientX - this._startX);
    if (newWidth < this.columnMinimumWidth) {
      newWidth = this.columnMinimumWidth;
    }
    this.width = newWidth;
  }

  handleFilterClick(event) {
    event.stopImmediatePropagation();
  }

  handleFilterFocus() {
    this.focused = true;
    this.resetHotkeyState();
    if (this.browse) {
      this.browse.dispatchEvent(new CustomEvent('browseHeaderCellActivated', {detail: {element: this}}));
    }
  }

  handleFilterBlur() {
    this.focused = false;
    if (this.browse) {
      this.browse.dispatchEvent(new CustomEvent('filterInputBlurred'));
    }
    if (this._currentValueChanged && this._currentValueChanged.isActive()) {
      this._currentValueChanged.flush();
    }
  }

  handleMouseDown() {
    if (!this.focused) {
      this.waitingForMouseUpAfterFocus = true;
    }
  }

  handleMouseUp() {
    if (this.waitingForMouseUpAfterFocus) {
      delete this.waitingForMouseUpAfterFocus;

      microTask.run(() => {
        this.renderRoot.querySelector('#filter').select();
      });
    }
  }

  handleFilterInput() {
    this.currentValue = this.renderRoot.querySelector('#filter').value;
    if (this.browse) {
      this.browse.currentlyFiltering = true;
      this.browse.chooseOnFilteringComplete = false;
    }
  }

  handleClick(event) {
    if (this.sortable !== false && this.browse) {
      this.browse.dispatchEvent(new CustomEvent('sortByColumn', {
        detail: {column: this, reset: !event.shiftKey}
      }));
    }
  }

  computeTooltipText(caption, width) {
    if (width < this.calculateTextWidth()) {
      return caption;
    }
  }

  calculateTextWidth(reset) {
    if (!this.caption) {
      return this.columnMinimumWidth;
    }
    if (!this.calculatedTextWidth || reset) {
      const labelComputedStyle = window.getComputedStyle(this.renderRoot.querySelector('#label'), null);
      this.calculatedTextWidth = measureTextWidth(this.caption.toUpperCase(), this.renderRoot.querySelector('#cell')) + (parseInt(labelComputedStyle.marginRight, 10) || 0);
      if (this.calculatedTextWidth < this.columnMinimumWidth) {
        this.calculatedTextWidth = this.columnMinimumWidth;
      }
      this.calculatedTextWidth = parseInt(this.calculatedTextWidth, 10) + 5;
    }
    return this.calculatedTextWidth;
  }
}

customElements.define('keli-browse-header-cell', KeliBrowseHeaderCell);